import { array } from './array.js';

/**
 * Class for inspecting, handling and processing JSON-stat and their associated metadata
 * such as language, dimensions, categories, translations and units.
 */
export class JsonStatReader {

  /**
   * JSON-stat
   * @type {null|JsonStat}
   */
  jsonStat = null;

  /**
   * The current language tag with the locale.
   * @type {String}
   */
  lang = null;

  /**
   * Use the default label?
   * @type {Boolean}
   * @private    if () {

   */
  #useDefaultLabel = true;

  /**
   * Initialize the module.
   * @param {JsonStat} jsonStat
   * @param {String} lang language tag with locale
   */
  constructor (jsonStat, lang) {
    this.jsonStat = jsonStat;
    this.lang = this.getLangLocale(lang);
    this.#useDefaultLabel = (this.lang === this.jsonStat.extension.defaultLanguage);
    this.shape = this.getDimSizes();
    this.stride = array.stride(this.shape);
  }

  /**
   * Return the language tag with the locale.
   *
   * Returns the matched language tag with the locale. If not matched,
   * the default language with the locale is returned.
   * @param lang language tag with or without the locale.
   * @return {String} language
   * @private
   */
  getLangLocale (lang) {
    const translation = this.jsonStat.extension.translation.language;
    let tag = lang;

    if (!translation.includes(lang)) {
      const langLocale = translation.find(item => {
        return item.split('-')[0] === lang;
      });
      tag = langLocale ? langLocale : this.jsonStat.extension.defaultLanguage;
    }

    return tag;
  }

  /**
   * Return the update time of the data set.
   * @return {Date}
   */
  getUpdate () {
    return new Date(this.jsonStat.updated);
  }

  /**
   * Return the (translated) label of the parameter.
   * @param {string} param name of the parameter
   * @return {String}
   */
  getLabelParam (param) {
    let label = this.jsonStat.extension.parameter[param].label;
    if (!this.#useDefaultLabel) {
      label = this.jsonStat.extension.translation.extension.parameter[param].label[this.lang];
    }
    return label;
  }

  /**
   * Return the (translated) label of the dimension.
   * @param {String} dimId
   * @return {String}
   */
  getLabel (dimId) {
    let label = this.jsonStat.dimension[dimId].label;
    if (!this.#useDefaultLabel) {
      label = this.jsonStat.extension.translation.dimension[dimId].label[this.lang];
    }

    return label;
  }

  /**
   * Returns the translated category of a dimension by dimension id and category label id.
   * Returns the default category label when the translated category is null.
   *
   * @param {String} dimId dimension id
   * @param {String} categId id of the category label
   *
   * @return {String} label
   */
  getCategoryLabel (dimId, categId) {
    let label;
    const dim = this.jsonStat.dimension[dimId];
    label = dim.category.label[categId];

    if (!this.#useDefaultLabel) {
      const translProp = this.jsonStat.extension.translation.dimension;
      label = translProp[dimId].category.label[categId][this.lang] ?? dim.category.label[categId];
    }

    return label;
  }

  /**
   * Return the label of the category by offset.
   * @param {Number} offset value index
   * @param {Number} dimIdx dimension index
   * @return {String}
   */
  getCategoryLabelByOffset (offset, dimIdx) {
    const dimId = this.jsonStat.id.at(dimIdx),
        categId = this.getCategoryIdByOffset(offset, dimIdx);

    return this.getCategoryLabel(dimId, categId);
  }

  /**
   * Return the index of the category.
   * @param {Number} offset value index
   * @param {Number} dimIdx dimension index
   * @return {Number}
   */
  getCategoryIndex (offset, dimIdx) {
    const stride = this.stride.at(dimIdx),
        // note: this.stride[dimIndex - 1] would have entry missing for the first dim
        prevStride = stride * this.shape.at(dimIdx);

    return Math.floor((offset % prevStride) / stride);
  }

  /**
   * Return the id of the category.
   * @return {String}
   * @param {String} dimId dimension id
   * @param {Number} categIdx category index
   */
  getCategoryId (dimId, categIdx) {
    return this.jsonStat.dimension[dimId].category.index[categIdx];
  }

  /**
   * Return the id of the category by offset.
   * @param {Number} offset value index
   * @param {Number} dimIdx dimension index
   * @return {String}
   */
  getCategoryIdByOffset (offset, dimIdx) {
    const dimId = this.jsonStat.id.at(dimIdx),
        categIdx = this.getCategoryIndex(offset, dimIdx);

    return this.jsonStat.dimension[dimId].category.index[categIdx];
  }

  /**
   * Return the (translated) unit of the value.
   * See NAFIDAS/scripts/php/class/JsonStat/Collator.php
   * @return {String}
   */
  getValueUnit () {
    const dimId = this.getDimIdMetric();
    const categId = this.jsonStat.dimension[dimId].category.index[0];
    let unit = this.getCategoryLabel(dimId, categId);
    if (this.jsonStat.extension.parameter.Bezugstyp !== null) {
      unit += ', ' + this.getLabelParam('Bezugstyp');
    }
    return unit;
  }

  /**
   * Returns the unit symbol of the error.
   * @return {string}
   */
  getErrorSymbol () {
    const dimId = this.getDimIdMetric();
    const unitIndex = this.jsonStat.dimension[dimId].category.index[1];
    let unit = this.getCategoryLabel(dimId, unitIndex);

    return unit.replace(/\s?±\s?/, '');
  }

  /**
   * Return the translation from the dictionary.
   * The dictionary contains translations of texts, which are neither
   * a dimension nor a parameter, but used in a result table.
   * @param {string} text text to translate
   * @return {String}
   */
  getTransl (text) {
    let label;

    label = text;
    if (!this.#useDefaultLabel) {
      const translProp = this.jsonStat.extension.translation.extension['non-parameter'][text];
      label = translProp[this.lang] ?? label;
    }

    return label;
  }

  /**
   * Return the id of the geo dimension.
   * Returns the id (index) of the (first) dimension with the geo role.
   * If there is no such role, null is returned.
   * In NAFIDAS JSON-stat this dimension is always the parameter regional demarcation.
   * @return {String}
   */
  getDimIdGeo () {
    return this.jsonStat.role.geo ? this.jsonStat.role.geo[0] : null;
  }

  /**
   * Return the id of the metric (concept) dimension.
   * Returns the id (index) of the (first) dimension with a metric role.
   * In NAFIDAS JSON-stat this dimension is always the parameter Zielgroesse.
   * @return {String}
   */
  getDimIdMetric () {
    return this.jsonStat.role.metric[0];
  }

  /**
   * Return the ids of all other dimensions.
   * Returns the ids (indexes) of the dimensions, which neither have a metric nor a geo role.
   * In NAFIDAS JSON-stat these dimensions are the parameters Befundeinheit.
   * @return {Array<String>}
   */
  getDimIdOthers () {
    return this.jsonStat.id.filter(value => {
      return (value !== this.getDimIdGeo()) && (value !== this.getDimIdMetric());
    });
  }

  /**
   * Return the size of the dimension(s).
   * @return {Array<Number>|Number}
   * @param {Number} [dimIdx] dimension index
   */
  getDimSizes (dimIdx = null) {
    return dimIdx === null ? this.jsonStat.size : this.jsonStat.size[dimIdx];
  }

  /**
   * Return the index of the specified dimension.
   *
   * @param {string} dimId - The dimension ID whose index is to be returned.
   * @return {number} The index of the specified dimension, or -1 if not found.
   */
  getDimIndex (dimId) {
    return this.jsonStat.id.indexOf(dimId);
  }

  /**
   * Check if the value at given offset is part of a nullrow.
   * @param {Number} offset value index
   * @return {Boolean}
   */
  isNullrow (offset) {
    let prop = this.jsonStat.extension.rendering.nullrows;

    return prop && prop.includes(offset);
  }

  /**
   * Return the size of the metric (concept) dimension.
   * Returns the size of the (first) dimension with a metric role.
   * In NAFIDAS JSON-stat this dimension is always the parameter Zielgroesse.
   * @return {Number}
   */
  getSizeOfMetric () {
    const idx = this.jsonStat.id.indexOf(this.getDimIdMetric());
    return this.jsonStat.size[idx];
  }

  /**
   * Return the number of decimals of the metric dimension.
   * @return {Number}
   */
  getDecimals () {
    const dimId = this.getDimIdMetric();
    const idx = this.jsonStat.dimension[dimId].category.index[0];
    return this.jsonStat.dimension[dimId].category.unit[idx].decimals;
  }
}